/**
 * Recurrence dates for homeowner tasks.
 * Intervals are added as written. Nothing is snapped onto the 7th, 14th, or 21st.
 * A set of tasks is spread across months and days so January is not a pile
 * and July is not left empty when another month has extras.
 */

const { localDateString, parseLocalDate } = require('../utils/dates');
const { bundlePlanned } = require('./monthBundle');

const MONTH_INDEX = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
};

const HORIZON_MONTHS = 12;
const MAX_POOL_ITEMS_PER_MONTH = 5;
const MONTHLY_POOL_TITLE = 'Monthly pool equipment check';

function isWeeklyPoolTask(task) {
  if (!task) return false;
  const freq = String(task.frequency || '').trim().toLowerCase();
  if (freq !== 'weekly' && freq !== 'every week') return false;
  const text = [task.title, task.task_name, task.category, task.required_feature]
    .filter(Boolean)
    .join(' ');
  return /pool/i.test(text);
}

function isPoolTask(task) {
  const text = [task && task.title, task && task.task_name, task && task.category, task && task.required_feature]
    .filter(Boolean)
    .join(' ');
  return /pool/i.test(text);
}

function isMonthlyPoolCatalogTask(task) {
  if (!task || isWeeklyPoolTask(task)) return false;
  const freq = String(task.frequency || '').trim().toLowerCase();
  if (freq !== 'monthly' && freq !== 'every month') return false;
  return isPoolTask(task);
}

function monthlyPoolTaskFields() {
  return {
    title: MONTHLY_POOL_TITLE,
    category: 'Pool',
    type: 'maintenance',
    frequency: 'Monthly',
    priority: 'medium',
    required_feature: 'Pool',
    month_to_perform: 'Every Month',
    agent_id: null,
    active: true,
    is_global: false,
  };
}

function pickMonthlyPoolTask(tasks) {
  const monthly = (tasks || []).filter(isMonthlyPoolCatalogTask);
  const named = monthly.find((task) =>
    String(task.title || '').trim().toLowerCase() === MONTHLY_POOL_TITLE.toLowerCase()
    && !task.agent_id
  );
  return named || monthly.find((task) => !task.agent_id) || monthly[0] || null;
}

async function ensureMonthlyPoolTask(admin, tasks) {
  if (pickMonthlyPoolTask(tasks)) return tasks;
  const fields = monthlyPoolTaskFields();
  const { data, error } = await admin
    .from('tasks')
    .insert(fields)
    .select('id, title, category, frequency, month_to_perform, property_types, required_feature, active, agent_id')
    .single();
  if (error) throw error;
  return (tasks || []).concat(data);
}

/**
 * Weekly pool chores are not generated. They become one monthly pool check,
 * the same cadence as the monthly reminder list.
 */
function tasksForSchedule(tasks) {
  const rest = [];
  let weeklyPool = null;
  let monthlyPool = null;
  (tasks || []).forEach((task) => {
    if (isWeeklyPoolTask(task)) {
      if (!weeklyPool) weeklyPool = task;
      return;
    }
    if (!monthlyPool && isMonthlyPoolCatalogTask(task)) monthlyPool = task;
    rest.push(task);
  });
  if (!monthlyPool && weeklyPool) {
    // Object.assign keeps this file CommonJS. Babel's object-spread helper is an ES module,
    // and the production bundle then refuses module.exports before the app can render.
    rest.push(Object.assign({}, weeklyPool, {
      frequency: 'Monthly',
      month_to_perform: 'Every Month',
      title: MONTHLY_POOL_TITLE,
    }));
  }
  return rest;
}

function capPoolItems(planned) {
  const used = new Map();
  return (planned || []).reduce((kept, row) => {
    if (!row || !isPoolTask(row.task)) {
      kept.push(row);
      return kept;
    }
    const dates = (row.dates || []).filter((date) => {
      const month = String(date).slice(0, 7);
      const count = used.get(month) || 0;
      if (count >= MAX_POOL_ITEMS_PER_MONTH) return false;
      used.set(month, count + 1);
      return true;
    });
    if (dates.length) kept.push(Object.assign({}, row, { dates }));
    return kept;
  }, []);
}

function frequencyStep(frequency) {
  const freq = String(frequency || '').trim().toLowerCase();
  if (!freq || freq === 'one-time' || freq === 'custom') return { once: true };
  if (freq === 'daily') return { days: 1 };
  if (freq === 'weekly' || freq === 'every week') return { days: 7 };
  if (freq === 'bi-weekly' || freq === 'biweekly' || freq === 'bi weekly') return { days: 14 };
  if (freq === 'monthly' || freq === 'every month') return { months: 1 };
  if (freq === 'quarterly' || freq === 'seasonally') return { months: 3 };
  if (freq === 'semi-annually' || freq === 'semiannually' || freq === 'semi-annually') return { months: 6 };
  if (freq === 'annually' || freq === 'annual' || freq === 'yearly') return { months: 12 };
  const years = /^every\s+(\d+)\s+years?$/.exec(freq);
  if (years) return { months: Number(years[1]) * 12 };
  if (freq.includes('every 7')) return { months: 84 };
  if (freq.includes('every 3')) return { months: 36 };
  if (freq.includes('every 2')) return { months: 24 };
  return { once: true };
}

function addStep(date, step) {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (step.days) {
    next.setDate(next.getDate() + step.days);
    return next;
  }
  if (step.months) {
    const day = next.getDate();
    next.setDate(1);
    next.setMonth(next.getMonth() + step.months);
    const last = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
    next.setDate(Math.min(day, last));
    return next;
  }
  return null;
}

function nextDueDate(currentDueDate, frequency) {
  const step = frequencyStep(frequency);
  if (!currentDueDate || !frequency || step.once) return null;
  const date = parseLocalDate(currentDueDate);
  if (!date) return null;
  const next = addStep(date, step);
  return next ? localDateString(next) : null;
}

function preferredMonth(monthToPerform) {
  if (!monthToPerform) return null;
  const key = String(monthToPerform).trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(MONTH_INDEX, key) ? MONTH_INDEX[key] : null;
}

function fullestIndex(buckets) {
  let index = 0;
  for (let i = 1; i < buckets.length; i += 1) {
    if (buckets[i].length > buckets[index].length) index = i;
  }
  return index;
}

function lightestIndex(buckets) {
  let index = 0;
  for (let i = 1; i < buckets.length; i += 1) {
    if (buckets[i].length < buckets[index].length) index = i;
  }
  return index;
}

function balanceMonths(tasks) {
  const buckets = Array.from({ length: 12 }, () => []);
  const flexible = [];
  (tasks || []).forEach((task) => {
    const month = preferredMonth(task.month_to_perform);
    if (month == null) flexible.push(task);
    else buckets[month].push(task);
  });
  flexible.forEach((task) => {
    buckets[lightestIndex(buckets)].push(task);
  });

  let guard = 0;
  while (buckets[6].length === 0 && guard < 24) {
    const from = fullestIndex(buckets);
    if (buckets[from].length < 2) break;
    buckets[6].push(buckets[from].pop());
    guard += 1;
  }

  guard = 0;
  while (guard < 500) {
    const full = fullestIndex(buckets);
    const empty = lightestIndex(buckets);
    if (buckets[full].length - buckets[empty].length <= 1) break;
    buckets[empty].push(buckets[full].pop());
    guard += 1;
  }
  return buckets;
}

function spreadDays(count) {
  const used = new Set();
  const days = [];
  for (let i = 0; i < count; i += 1) {
    let day = Math.round(((i + 1) * 28) / (count + 1));
    day = Math.min(28, Math.max(1, day));
    let step = 1;
    while (used.has(day)) {
      const up = day + step;
      const down = day - step;
      if (up <= 28 && !used.has(up)) day = up;
      else if (down >= 1 && !used.has(down)) day = down;
      else step += 1;
      if (step > 28) break;
    }
    used.add(day);
    days.push(day);
  }
  return days;
}

function horizonEnd(today) {
  return new Date(today.getFullYear(), today.getMonth() + HORIZON_MONTHS + 1, 0);
}

function firstOnOrAfter(today, month, day) {
  let date = new Date(today.getFullYear(), month, day);
  if (date < today) date = new Date(today.getFullYear() + 1, month, day);
  return date;
}

function datesFromAnchor(anchor, step, today, end) {
  const dates = [];
  let cursor = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  let guard = 0;
  const cap = step.days === 1 ? 366 : 60;
  while (cursor < today && guard < 40) {
    const moved = addStep(cursor, step);
    if (!moved) break;
    cursor = moved;
    guard += 1;
  }
  while (cursor <= end && dates.length < cap && guard < cap + 40) {
    dates.push(localDateString(cursor));
    if (step.once || (step.months && step.months >= 12)) break;
    const moved = addStep(cursor, step);
    if (!moved) break;
    cursor = moved;
    guard += 1;
  }
  if (dates.length === 0) dates.push(localDateString(cursor < today ? anchor : cursor));
  return dates;
}

function firstMonthWeek(today, anchor) {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const day = Math.round((anchor.getTime() - start.getTime()) / 86400000);
  if (day < 0 || day > 27) return null;
  return Math.floor(day / 7);
}

/**
 * First-month anchors: weeks 1-4 (four 7-day windows from today) hold at most
 * `cap` tasks. Overflow anchors move forward a week at a time into month 2.
 * Later occurrences of a task are rebuilt from the new anchor.
 */
function applyFirstMonthWeeklyCap(planned, today, cap) {
  const limit = Number(cap);
  if (!limit || limit < 1) return planned || [];
  const counts = [0, 0, 0, 0];
  const end = horizonEnd(today);
  const next = [];
  (planned || []).forEach((row) => {
    if (!row || !row.dates || !row.dates.length || !row.task) {
      next.push(row);
      return;
    }
    const step = frequencyStep(row.task.frequency);
    let anchor = parseLocalDate(row.dates[0]);
    let guard = 0;
    while (anchor && guard < 16) {
      const week = firstMonthWeek(today, anchor);
      if (week == null) break;
      if (counts[week] < limit) {
        counts[week] += 1;
        break;
      }
      anchor = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + 7);
      guard += 1;
    }
    const dates = anchor ? datesFromAnchor(anchor, step, today, end) : row.dates;
    next.push(Object.assign({}, row, { dates }));
  });
  return next;
}

function scheduleTasks(tasks, now = new Date(), options) {
  const source = tasksForSchedule(tasks);
  const today = parseLocalDate(now) || new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = horizonEnd(today);
  const anchored = [];
  const rolling = [];
  (source || []).filter(Boolean).forEach((task) => {
    const step = frequencyStep(task.frequency);
    if (step.days || (step.months && step.months < 12)) rolling.push(task);
    else anchored.push(task);
  });

  const planned = [];
  balanceMonths(anchored).forEach((bucket, month) => {
    const days = spreadDays(bucket.length);
    bucket.forEach((task, index) => {
      const anchor = firstOnOrAfter(today, month, days[index]);
      const step = frequencyStep(task.frequency);
      planned.push({ task, dates: datesFromAnchor(anchor, step, today, end) });
    });
  });

  const groups = new Map();
  rolling.forEach((task) => {
    const step = frequencyStep(task.frequency);
    const key = step.days ? `d${step.days}` : `m${step.months}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(task);
  });
  groups.forEach((group) => {
    const step = frequencyStep(group[0].frequency);
    const days = spreadDays(group.length);
    group.forEach((task, index) => {
      let anchor;
      if (step.days) {
        anchor = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (index % 7) + 1);
      } else if (step.months === 1) {
        anchor = new Date(today.getFullYear(), today.getMonth() + 1, days[index]);
      } else {
        const monthPref = preferredMonth(task.month_to_perform);
        const month = monthPref == null ? (today.getMonth() + index) % 12 : monthPref;
        anchor = firstOnOrAfter(today, month, days[index]);
      }
      planned.push({ task, dates: datesFromAnchor(anchor, step, today, end) });
    });
  });
  const cap = options && options.firstMonthWeeklyCap;
  const placed = cap ? applyFirstMonthWeeklyCap(planned, today, cap) : planned;
  return bundlePlanned(capPoolItems(placed), today, options && options.existing);
}

function firstDueDate(task, now = new Date()) {
  const planned = scheduleTasks([task], now);
  const dates = planned[0] && planned[0].dates;
  return dates && dates[0] ? dates[0] : localDateString(now);
}

module.exports = {
  HORIZON_MONTHS,
  MAX_POOL_ITEMS_PER_MONTH,
  MONTHLY_POOL_TITLE,
  isWeeklyPoolTask,
  isMonthlyPoolCatalogTask,
  monthlyPoolTaskFields,
  pickMonthlyPoolTask,
  ensureMonthlyPoolTask,
  frequencyStep,
  addStep,
  applyFirstMonthWeeklyCap,
  nextDueDate,
  balanceMonths,
  spreadDays,
  scheduleTasks,
  firstDueDate,
};
